import axios from 'axios';
import { API_ROUTES } from '@/const/apiRoutes';
import { isPublicPath } from '@/const/appRoutes';
import { decodeJWT } from '@/utils/jwtDecoder';
import { isSubscriptionError, handleSubscriptionError } from '@/utils/subscriptionErrorHandler';
import { savePostLoginRedirect } from '@/utils/authRedirect';

// Ensure BASE_URL is defined in API_ROUTES or replace with the actual base URL string
const BASE_URL = API_ROUTES.BASE_URL

// Request queuing system to prevent multiple simultaneous token refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Create an instance of axios with the base URL
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Required for cross-domain cookie authentication
  timeout: 10000, // 10 second timeout to prevent hanging requests
});

// Request interceptor to add the Authorization header
api.interceptors.request.use(
  // eslint-disable-next-line complexity
  async (config) => {
    // Check if BASE_URL is set, if not, reject the request early
    if (!BASE_URL) {
      return Promise.reject(new Error('API base URL is not configured. Please set VITE_APP_BACKEND_DOMAIN_NAME environment variable.'));
    }

    const token = localStorage.getItem('token');
    
    // Only add Authorization header if token exists and is valid
    // For cross-subdomain scenarios, rely on cookies instead of localStorage tokens
    if (token && config.headers) {
      // Validate token before sending - MUST have 'id' field or backend will reject it
      try {
        const decodedToken = decodeJWT(token);
        
        // Only send token if it's valid, not expired, AND has required 'id' field
        if (decodedToken) {
          // Check if token is expired
          const isExpired = decodedToken.exp && decodedToken.exp * 1000 < Date.now();
          
          // Backend REQUIRES token to have 'id' field - don't send without it
          const hasId = decodedToken.id !== undefined && decodedToken.id !== null;
          
          if (isExpired) {
            // Token is expired, remove it and rely on cookies
            console.warn('Token expired, removing from localStorage and relying on cookies');
            localStorage.removeItem('token');
          } else if (!hasId) {
            // Token missing required id field - DON'T send it, rely on cookies only
            // Backend will reject tokens without id, so we skip Authorization header
            console.warn('Token missing required id field - not sending in Authorization header, relying on cookies only');
            // Don't remove token from localStorage - cookies might work
            // But don't send it in Authorization header
          } else {
            // Token is valid, not expired, and has required id field - send it
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } else {
          // Invalid token format, remove it and rely on cookies
          console.warn('Invalid token format, removing from localStorage and relying on cookies');
          localStorage.removeItem('token');
        }
      } catch (err) {
        // Token is malformed, remove it and rely on cookies
        console.warn('Malformed token, removing from localStorage and relying on cookies:', err);
        localStorage.removeItem('token');
      }

      // Let axios set Content-Type automatically for FormData (multipart/form-data + boundary).
      // Forcing application/json here would break file uploads.
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    // Decode JWT and save the user ID to localStorage if not already saved
    if (token && !localStorage.getItem('user_id')) {
      try {
        const decodedToken = decodeJWT(token);
        if (decodedToken && decodedToken.id) {
          localStorage.setItem('user_id', decodedToken.id);
          console.log('User ID stored in localStorage:', decodedToken.id);
        } else {
          console.error('Failed to decode token or extract user ID');
        }
      } catch (err) {
        // If token is malformed, log error but don't crash the interceptor
        // This prevents API requests from failing due to corrupted tokens
        console.error('Failed to decode token in request interceptor:', err);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Detect Django REST / SimpleJWT "token not valid" error responses.
 * These arrive when the Authorization header contains a structurally valid
 * but rejected JWT (revoked, wrong signing key, cross-subdomain mismatch, …).
 */
function isTokenNotValidError(err: any): boolean {
  const data = err.response?.data;
  if (!data) return false;
  if (data.code === 'token_not_valid') return true;
  if (typeof data.detail === 'string' && data.detail.includes('token')) return true;
  if (
    Array.isArray(data.messages) &&
    data.messages.some((m: any) => m.message?.includes('Token'))
  ) return true;
  return false;
}

/** Auth-related URLs that must never trigger the refresh/retry cycle. */
function isAuthEndpoint(url: string | undefined): boolean {
  return url === API_ROUTES.USER_LOGIN
    || url === API_ROUTES.USER_LOGOUT
    || url === API_ROUTES.USER_JWT_REFRESH;
}

/**
 * After all refresh attempts failed, redirect the browser to the login page
 * while preserving the current URL so the user lands back after re-auth.
 */
function hardRedirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname;
  if (isPublicPath(currentPath)) return;

  const fullPath = window.location.pathname + window.location.search;
  savePostLoginRedirect(fullPath);
  window.location.href = `/login?redirect=${encodeURIComponent(fullPath)}`;
}

// Response interceptor for handling token refresh and subscription errors
api.interceptors.response.use(
  (response) => response,
  // eslint-disable-next-line complexity
  async (error) => {
    const originalRequest = error.config;

    if (isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    if (isSubscriptionError(error)) {
      handleSubscriptionError(error);
      return Promise.reject(error);
    }

    // Backend explicitly rejected the JWT — clear it and retry with cookies.
    // If the cookie-only retry also returns 401, reset _retry so the
    // refresh handler below gets a chance to recover the session.
    if (isTokenNotValidError(error)) {
      console.warn('Backend rejected token as invalid, retrying with cookies only');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');

      if (originalRequest.headers) {
        delete originalRequest.headers['Authorization'];
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          return await api(originalRequest);
        } catch (retryError: any) {
          if (retryError.response?.status === 401) {
            originalRequest._retry = false;
          } else {
            return Promise.reject(error);
          }
        }
      }
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        // Set _retry flag to prevent this request from attempting refresh again if it gets 401 after retry
        originalRequest._retry = true;
        
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (token && originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to get refresh token from localStorage or cookies (backend will read from cookies)
        const refreshToken = localStorage.getItem('refresh_token');
        
        // Backend accepts refresh token in body or cookies
        const refreshPayload = refreshToken ? { jwt_refresh: refreshToken } : {};
        
        const { data } = await api.post(API_ROUTES.USER_JWT_REFRESH, refreshPayload);

        // Verify that jwt_access is present in the response
        if (!data.jwt_access) {
          // If refresh succeeded but no token was returned, reject all queued requests
          const error = new Error('Token refresh response missing jwt_access');
          processQueue(error, null);
          isRefreshing = false;
          throw error;
        }

        // Validate new token before storing - MUST have 'id' field
        // Backend REQUIRES tokens to have 'id' field - don't store without it
        let tokenValid = false;
        try {
          const decodedToken = decodeJWT(data.jwt_access);
          if (decodedToken && decodedToken.exp) {
            // Check if token is expired
            const isExpired = decodedToken.exp * 1000 < Date.now();
            
            // Backend REQUIRES token to have 'id' field
            const hasId = decodedToken.id !== undefined && decodedToken.id !== null;
            
            if (isExpired) {
              console.error('Refreshed token is already expired');
            } else if (!hasId) {
              console.error('Refreshed token missing required id field - backend will reject it');
            } else {
              // Token is valid, not expired, and has required id field - store it
              localStorage.setItem('token', data.jwt_access);
              localStorage.setItem('user_id', decodedToken.id);
              console.log('User ID updated in localStorage:', decodedToken.id);
              tokenValid = true;
            }
          } else {
            console.error('Refreshed token missing exp field');
          }
        } catch (err) {
          // If new token is malformed, log error but don't crash the interceptor
          console.error('Failed to decode new token in response interceptor:', err);
        }
        
        if (!tokenValid) {
          // Token refresh returned invalid token (missing id or expired), reject all queued requests
          const error = new Error('Token refresh response contains invalid token (missing id, missing exp, or expired)');
          processQueue(error, null);
          isRefreshing = false;
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          throw error;
        }
        
        // Update original request Authorization header with new access token
        // Token is guaranteed to have 'id' field at this point
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${data.jwt_access}`;
        }

        // Process queued requests with valid token
        processQueue(null, data.jwt_access);
        isRefreshing = false;

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_id');

        hardRedirectToLogin();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;